<template>
    <div
        class="sub-assignment-edit-header"
        :class="!groupIn && 'organizational-header'">
        <div
            class="component-title"
            :class="groupIn && 'order-1'">
            <h5
                class="title"
                :class="inlineEditInProgress && 'editing'">
                <span class="assignment-title">
                    {{ subAssignmentIn.name }}
                    <b-button
                        class="btn-edit btn-cancel"
                        v-if="canEditPortfolio"
                        variant="primary-icon-only"
                        @click="inlineEditInProgress = true">
                    </b-button>
                </span>
                <div
                    class="inline-assignment-title-edit"
                    v-if="inlineEditInProgress && canEditPortfolio">
                    <b-form-input v-model="subAssignment.name"></b-form-input>
                    <b-button
                        class="btn-checked btn-update"
                        variant="success-icon-only"
                        @click="updateAssignmentTitle">
                    </b-button>
                    <b-button
                        class="btn-times btn-cancel"
                        variant="primary-icon-only"
                        @click="cancelAssignmentTitleUpdate">
                    </b-button>
                </div>
                <div
                    class="deadline"
                    v-if="groupIn && subAssignmentIn.hasDeadline()">
                    <i class="fad fa-alarm-exclamation icon"></i
                    >{{ subAssignmentIn.deadlines[0].agenda_item.full_date_time }}
                </div>
            </h5>
        </div>

        <div
            class="sub-assignment-edit-tools order-0"
            v-if="groupIn">
            <div
                class="group-pair-status"
                v-b-tooltip:hover
                v-if="subAssignmentIn.assignment_cooperations.length > 0"
                role="button"
                :title="$t('assignment.collaborate_active')"
                @click="openTeacherCoop()">
                <i class="icon fa fa-users fa-fw"></i>
            </div>

            <div class="context-nav context-nav-sub-assignment">
                <b-dropdown
                    class="ellipsis-nav sub-assignment-nav"
                    no-caret
                    variant="primary"
                    right
                    toggle-class="sub-assignment-edit-context-menu">
                    <template v-slot:button-content>
                        <i class="fas fa-ellipsis-v"></i>
                    </template>

                    <b-dropdown-item
                        class="btn-edit btn-edit-sub-assignment-title"
                        v-if="canEditPortfolio"
                        v-b-modal="'edit-sub-assignment-name'"
                        @click="$event.stopPropagation()">
                        {{ $t('portfolio.set_name_sub_assignment') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-medal btn-sub-assignment-rating-settings"
                        v-if="canEditPortfolio && groupIn"
                        v-b-modal="'sub-assignment-edit-rating-' + subAssignmentIn.id"
                        @click="$event.stopPropagation()">
                        {{ $t('portfolio.set_rating_sub_assignment') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-sub-assignment-deadline"
                        v-if="canEditPortfolio && groupIn"
                        v-b-modal="'sub-assignment-edit-deadline-modal'"
                        @click="$event.stopPropagation()">
                        <i class="btn-clock"></i>{{ $t('portfolio.edit_deadline') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="copy-sub-assignment"
                        v-if="canEditPortfolio"
                        @click.prevent.stop="copySubAssignment">
                        <i class="btn-duplicate"></i>{{ $t('portfolio.copy_sub_assignment') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-sub-assignment-cooperation"
                        @click="
                            $event.stopPropagation();
                            onClickCooperation();
                        ">
                        <i class="btn-users"></i>{{ $t('assignment.collaborate') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-remove-sub-assignment"
                        v-if="canEditPortfolio"
                        @click.prevent.stop="onRemove()">
                        <i class="btn-remove"></i>{{ $t('portfolio.delete_sub_assignment') }}
                    </b-dropdown-item>
                </b-dropdown>
            </div>

            <design-2021-assignment-edit-sub-assignment-cooperation-modal-component
                v-if="groupIn"
                :sub-assignment-in="subAssignmentIn"
                @openTeacherCoop="openTeacherCoop"
                :group-id-in="groupIn.id"
                :key="'sub-assignment-cooperation-' + subAssignmentIn.id">
            </design-2021-assignment-edit-sub-assignment-cooperation-modal-component>

            <design-2021-assignment-edit-match-maker-modal-component
                :group-id-in="groupIn.id"
                :ref="'match-maker-modal'">
            </design-2021-assignment-edit-match-maker-modal-component>

            <design-2021-assignment-edit-sub-assignment-edit-deadline-modal-component
                v-if="groupIn"
                :sub-assignment-in="subAssignmentIn"
                :group-in="groupIn"
                :key="'sub-assignment-deadline-' + subAssignmentIn.id">
            </design-2021-assignment-edit-sub-assignment-edit-deadline-modal-component>

            <design-2021-portfolio-edit-rating-modal-component
                v-if="groupIn"
                :object-in="subAssignmentIn"
                :key-name="'sub-assignment-edit-rating-' + subAssignmentIn.id"
                :show-grade-options="false"
                :title="$t('portfolio.rating_sub_assignment') + ': ' + subAssignmentIn.name"
                @on-save="saveRating($event)">
            </design-2021-portfolio-edit-rating-modal-component>
        </div>

        <div
            class="organizational-tools"
            v-else>
            <div class="context-nav context-nav-sub-assignment">
                <b-dropdown
                    class="ellipsis-nav sub-assignment-nav"
                    no-caret
                    variant="primary"
                    right
                    toggle-class="sub-assignment-edit-context-menu">
                    <template v-slot:button-content>
                        <i class="fas fa-ellipsis-v"></i>
                    </template>

                    <b-dropdown-item
                        class="btn-edit btn-edit-sub-assignment-title"
                        v-if="canEditPortfolio"
                        v-b-modal="'edit-sub-assignment-name'">
                        {{ $t('portfolio.set_name_sub_assignment') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="copy-sub-assignment"
                        v-if="canEditPortfolio"
                        @click.prevent.stop="copySubAssignment">
                        <i class="btn-duplicate"></i>{{ $t('portfolio.copy_sub_assignment') }}
                    </b-dropdown-item>

                    <b-dropdown-item
                        class="btn-remove-sub-assignment"
                        v-if="canEditPortfolio"
                        @click.prevent.stop="onRemove">
                        <i class="btn-remove"></i>{{ $t('portfolio.delete_sub_assignment') }}
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>

        <design-2021-assignment-edit-sub-assignment-title-edit-modal-component
            :group-in="groupIn"
            @sub-assignment-updated="updateSubAssignment"
            :sub-assignment-in="subAssignmentIn">
        </design-2021-assignment-edit-sub-assignment-title-edit-modal-component>
    </div>
</template>

<script>
export default {
    name: 'design-2021-assignment-edit-sub-assignment-header-component',
    props: {
        subAssignmentIn: {
            type: Object,
            required: true,
        },
        assignmentIn: {
            type: Object,
            required: true,
        },
        groupIn: {
            type: Object,
            required: false,
            default: null,
        },
        canEditPortfolio: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            subAssignment: this.subAssignmentIn,
            inlineEditInProgress: false,
            initialSubTitle: this.subAssignmentIn.name,
        };
    },

    methods: {
        onClickCooperation() {
            if (this.assignmentIn.hasAssignmentCooperation()) {
                this.alert.notice(
                    'Samenwerken',
                    'Er staat al een samenwerking op de gehele opdracht, hierdoor kan het niet meer op een deelopdracht gezet worden.',
                );
            } else {
                this.$bvModal.show('sub-assignment-cooperation-modal');
            }
        },
        saveRating(data) {
            this.alert.saving();

            if (!this.subAssignmentIn.portfolio_rating) {
                this.axios.post(this.routes.portfolioSubAssignmentRating(), data).then(
                    (response) => {
                        this.subAssignmentIn.addRelation('portfolio_rating', response.data.rating);
                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            } else {
                this.axios.post(this.routes.portfolioSubAssignmentRatingUpdate(), data).then(
                    (response) => {
                        this.subAssignmentIn.portfolio_rating.update(response.data.rating);
                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            }
        },
        onRemove() {
            this.alert
                .confirm('Deelopdracht verwijderen', 'Weet je zeker dat je deze deelopdracht wil verwijderen?')
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving(
                            'Deelopdracht verwijderen',
                            'De deelopdracht wordt verwijderd, één moment geduld a.u.b.',
                        );
                        this.axios.delete(this.routes.subAssignmentDelete(this.subAssignmentIn.id)).then(
                            (response) => {
                                if (this.groupIn) {
                                    this.subAssignmentIn.remove();
                                }
                                this.$emit('sub-assignment-removed', this.subAssignmentIn.id);
                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },

        copySubAssignment() {
            this.alert.saving();

            this.axios.post(this.routes.subAssignmentCopy(this.subAssignmentIn.id)).then(
                (response) => {
                    if (this.groupIn) {
                        this.assignmentIn.addRelation('sub_assignments', response.data);
                    }
                    this.$emit('sub-assignment-added', response.data);
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        openTeacherCoop(assignmentCooperation = this.subAssignmentIn.assignment_cooperations[0]) {
            this.$refs['match-maker-modal'].show(assignmentCooperation);
        },
        updateSubAssignment(updatedSubAssignment) {
            this.$emit('sub-assignment-updated', updatedSubAssignment);
        },

        updateAssignmentTitle() {
            if (this.subAssignment.name === null || this.subAssignment.name === '') {
                this.alert.error('Titel deelopdracht is niet bijgewerkt', 'De titel mag niet leeg zijn');
                return;
            }

            this.alert.saving();

            const data = {
                name: this.subAssignment.name,
            };

            this.axios.post(this.routes.subAssignmentUpdate(this.subAssignmentIn.id), data).then(
                (response) => {
                    if (this.groupIn) {
                        this.subAssignmentIn.update({ name: data.name });
                    }
                    this.updateSubAssignment(response.data);
                    this.initialSubTitle = this.subAssignment.name;
                    this.inlineEditInProgress = false;
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        cancelAssignmentTitleUpdate() {
            this.subAssignment.name = this.initialSubTitle;
            this.inlineEditInProgress = false;
        },
    },
};
</script>

<style scoped lang="scss">
.sub-assignment-edit-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');

    .component-title {
        margin-bottom: 0;
        width: 100%;
    }

    .title {
        width: 100%;

        &.editing {
            .assignment-title {
                display: none;
                opacity: 0;
            }

            .inline-assignment-title-edit {
                opacity: 1;
            }
        }

        .deadline {
            margin-top: map-get($grid-gutter-spacing, 'eight');
        }
    }

    .assignment-title,
    .inline-assignment-title-edit {
        display: flex;
        align-items: center;
        transition: all 0.8s ease;

        .btn {
            &:before {
                margin: 0;
            }
        }
    }

    .assignment-title {
        position: relative;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
        opacity: 1;
    }

    .inline-assignment-title-edit {
        column-gap: map-get($grid-gutter-spacing, 'eight');
        opacity: 0;

        input {
            margin: 0;
            border: 1px solid var(--primary);
            padding: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'fifteen');
        }

        .btn {
            font-size: 1.125rem;
        }
    }

    .deadline {
        color: $danger;
        font-size: $base-font-size;

        .icon {
            margin-right: map-get($grid-gutter-spacing, 'eight');
        }
    }

    .sub-assignment-edit-tools {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        justify-content: flex-end;
        flex: 1 0 100%;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
        margin-bottom: map-get($grid-gutter-spacing, 'eight');

        .group-pair-status {
            .icon {
                color: var(--primary);
            }
        }
    }

    ::v-deep .ellipsis-nav {
        button.dropdown-toggle {
            &:not(:disabled):not(.disabled):active,
            &:not(:disabled):not(.disabled):active {
                background-color: var(--primary);
            }
        }
    }
}

.context-nav-sub-assignment ::v-deep {
    .sub-assignment-nav {
        button.dropdown-toggle {
            color: $white !important;
            background-color: var(--primary);
            padding: map-get($grid-gutter-spacing, 'eight') map-get($grid-gutter-spacing, 'fifteen');
        }

        .dropdown-menu {
            border: 1px solid $border-color;
        }

        .btn-sub-assignment-rating-settings,
        .btn-sub-assignment-cooperation,
        .btn-sub-assignment-deadline {
            i,
            &:before {
                color: var(--primary);
            }
        }

        .btn-sub-assignment-deadline,
        .btn-sub-assignment-cooperation,
        .btn-remove-sub-assignment {
            font-size: $default-font-size;

            .dropdown-item {
                display: flex;
                align-items: center;

                i {
                    margin-right: 6px;
                    font-size: $base-font-size;
                }
            }

            &:before {
                margin-right: map-get($grid-gutter-spacing, 'eight');
            }
        }

        .copy-sub-assignment {
            i {
                color: var(--primary);
            }
        }
    }
}

.organizational-header {
    .component-title {
        align-self: center;
    }

    .organizational-tools {
        display: flex;
        align-self: baseline;
        align-items: center;
        margin-left: auto;
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .sub-assignment-edit-header {
        column-gap: $grid-gutter-width;
        .component-title {
            flex: 1 1 0;
        }

        .sub-assignment-edit-tools {
            flex: 0 1 auto;
            margin-bottom: 0;
            align-self: baseline;
            order: 1;
        }
    }
}
</style>
